import { TextManager, useTexts } from "Components/TextContext";
import { Hint, Label } from "Components/TextContext/commonComponentEnums";
import React, { useEffect } from "react";

import { Props as StoreblocksAlertProps } from "@storeblocks/alert";
import { StyledAlert } from "App/global-style";
import { AccessibleCallUsMessage } from "Components/Form/AccessibleCallUsMessage";
import { AlertType } from "Components/Form/Alert";
import { RadioButtonTrueFalse } from "Components/Form/RadioButton";
import { getYesOrNoOptions } from "Components/Form/RadioButton/radioButtonUtils";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { usePartnerId } from "Store/partners/partnerHooks";
import { Partner } from "Store/partners/partnerInterfaces";
import { isAkademikerneTheme } from "Utils/partnerUtils";
import { getError } from "Utils/productUtils";
import { FormikProps } from "formik";
import { CitizenshipFormValues } from "./interfaces";

interface Props {
  formProps: FormikProps<CitizenshipFormValues>;
}

export const Citizenship = ({ formProps }: Props): React.ReactElement => {
  const isAdvisor = useIsAdvisorSelector();
  const { getText } = useTexts();
  const partnerId = usePartnerId();

  const { errorType, stayingInNorwayErrorTitle, stayingInNorwayErrorText } =
    getAlertProps(isAdvisor, partnerId as Partner);
  const showStayingInNorwayError =
    formProps.values.isScandinavianCitizen === BooleanFormType.FALSE &&
    formProps.values.isStayingInNorwayForMoreThanFiveYears ===
      BooleanFormType.FALSE;

  useEffect(() => {
    formProps.setFieldTouched("isStayingInNorwayForMoreThanFiveYears", false);
  }, [formProps.values.isScandinavianCitizen]);

  return (
    <>
      <RadioButtonTrueFalse
        name="isScandinavianCitizen"
        id="isScandinavianCitizen"
        label={getText(Label.IS_NORDIC_CITIZEN)}
        hint={getText(Hint.IS_NORDIC_CITIZEN)}
        options={getYesOrNoOptions(
          "isScandinavianCitizen-yes",
          "isScandinavianCitizen-no",
        )}
        direction="vertical"
        error={getError("isScandinavianCitizen", formProps)}
      />
      {formProps.values.isScandinavianCitizen === BooleanFormType.FALSE && (
        <RadioButtonTrueFalse
          name="isStayingInNorwayForMoreThanFiveYears"
          id="isStayingInNorwayForMoreThanFiveYears"
          label={getText(Label.IS_IN_NORWAY_OVER_FIVE_YEARS)}
          options={getYesOrNoOptions(
            "isStayingInNorwayForMoreThanFiveYears-yes",
            "isStayingInNorwayForMoreThanFiveYears-no",
          )}
          direction="vertical"
          error={getError("isStayingInNorwayForMoreThanFiveYears", formProps)}
        />
      )}
      {showStayingInNorwayError && (
        <StyledAlert
          title={stayingInNorwayErrorTitle}
          description={stayingInNorwayErrorText}
          type={errorType ?? "error"}
          fluid
          alertId="stayingInNorway"
          alertType={AlertType.STOPPER_ALERT}
        >
          {!isAdvisor && <AccessibleCallUsMessage />}
        </StyledAlert>
      )}
    </>
  );
};

const getAlertProps = (
  isAdvisor: boolean,
  partnerId: Partner,
): {
  stayingInNorwayErrorTitle: string;
  stayingInNorwayErrorText: string;
  errorType?: StoreblocksAlertProps["variant"];
} => {
  const { getText } = TextManager;

  if (isAdvisor) {
    if (isAkademikerneTheme(partnerId)) {
      return {
        errorType: "info",
        stayingInNorwayErrorTitle:
          "Vi kan dessverre ikke tilby kunden individuell personforsikring",
        stayingInNorwayErrorText:
          "Vi tilbyr bare individuell personforsikring til statsborgere i nordiske land eller personer som har bodd sammenhengende i Norge de siste 5 årene. For mer informasjon se tegningsbestemmelser.",
      };
    }

    return {
      stayingInNorwayErrorTitle: getText(
        "citizenshippepform.alerttitle.cantofferinsuranceonlineadvisor",
      ),
      stayingInNorwayErrorText: getText(
        "citizenshippepform.alertdescription.isstayinginnorwayformorethanfiveyearsadvisor",
      ),
    };
  }

  return {
    stayingInNorwayErrorTitle: getText(
      "citizenshippepform.alerttitle.cantofferinsuranceonline",
    ),
    stayingInNorwayErrorText: getText(
      "citizenshippepform.alertdescription.isstayinginnorwayformorethanfiveyears",
    ),
  };
};
